import { AxiosResponse } from 'axios';
import { addTask } from 'domain-task';
import APIManager from '../../utilities/api-manager';
import { transformAnnouncementsArchive } from '../../utilities/announcementsUtilites';
import { debounce, get, getConfig } from '../../helpers';
import {
    API_ANNOUNCEMENTS_ARCHIVE,
    API_DISMISS_ANNOUNCEMENTS,
    API_FETCH_ANNOUNCEMENTS_SUMMARY,
    API_MARK_ANNOUNCEMENTS_VIEWED,
} from '../actionTypes';
import {
    saveAnnouncementDismissals,
    loadAnnouncementDismissals,
    saveAnnouncementViewed,
    publishAnnouncementSyncMessage,
    loadAnonymousLastSummaryIds,
    saveAnonymousLastSummaryIds,
    saveAnonymousCurrentSessionNewIds,
    broadcastAnonNewIds,
    saveAnnouncementsSummaryCache,
    saveAnnouncementsArchiveCache,
} from './announcements.storage';

const GUEST_ANNOUNCEMENTS_CACHE_SCOPE = 'shared';

const fetchAnnouncementsSummary = (): AppThunkAction<FetchAnnouncementsSummaryAction> => (dispatch, getState) => {
    const isLoggedIn = get(getState(), 'user.isLoggedIn', false);
    const fetchTask = APIManager.getAnnouncementsSummary().then(response => {
        dispatch({ response: response.data, type: API_FETCH_ANNOUNCEMENTS_SUMMARY, isLoggedIn });

        // Cache for non-logged-in users so language reloads can hydrate without re-fetching
        if (!isLoggedIn) {
            saveAnnouncementsSummaryCache(GUEST_ANNOUNCEMENTS_CACHE_SCOPE, response.data);
        }

        if (!isLoggedIn) {
            const currentSummaryIds = Array.isArray(response.data.Announcements)
                ? response.data.Announcements.map((announcement: AnnouncementItem) => announcement.KeyMarketplaceAnnouncements)
                : [];
            const previousSummaryIds = loadAnonymousLastSummaryIds();
            const newAnnouncementIds = previousSummaryIds === null
                ? currentSummaryIds
                : currentSummaryIds.filter(id => !new Set(previousSummaryIds).has(id));

            saveAnonymousCurrentSessionNewIds(newAnnouncementIds);

            // BroadcastChannel fallback for tabs that are currently open/listening.
            broadcastAnonNewIds(newAnnouncementIds);

            saveAnonymousLastSummaryIds(currentSummaryIds);
        }
    });
    addTask(fetchTask);
};

const fetchAnnouncementsArchive = (): AppThunkAction<FetchAnnouncementsArchiveAction> => (dispatch, getState) => {
    const fetchTask = APIManager.getAnnouncementsArchive().then((response: AxiosResponse<AnnouncementItem[]>) => {
        if (response.data) {
            const transformed = transformAnnouncementsArchive(response.data);
            dispatch({ announcementsArchive: transformed, type: API_ANNOUNCEMENTS_ARCHIVE });

            // Cache for non-logged-in users so language reloads can hydrate without re-fetching
            const isLoggedIn = get(getState(), 'user.isLoggedIn', false);
            if (!isLoggedIn) {
                saveAnnouncementsArchiveCache(GUEST_ANNOUNCEMENTS_CACHE_SCOPE, transformed);
            }
        }
    });
    addTask(fetchTask);
};

const dismissDebounceDuration = getConfig('components.announcementThrottleDuration');

let dismissedAnnouncementsQueue: DismissAnnouncementRequestPayload[] = [];

const queueDismissedAnnouncements = (announcementIds: string[]) => {
    const dismissedDateTime = new Date().toISOString();

    announcementIds.forEach(announcementId => {
        const isAlreadyQueued = dismissedAnnouncementsQueue.some(item => item.keyMarketPlaceAnnouncements === announcementId);
        if (!isAlreadyQueued) {
            dismissedAnnouncementsQueue.push({
                keyMarketPlaceAnnouncements: announcementId,
                dismissedAt: dismissedDateTime
            });
        }
    });
};

const flushDismissedAnnouncements = debounce(() => {
    if (!dismissedAnnouncementsQueue.length) {
        return;
    }

    const queuedDismissals = [...dismissedAnnouncementsQueue];
    const dismissTask = APIManager.dismissAnnouncements(queuedDismissals).then(() => {
        dismissedAnnouncementsQueue = dismissedAnnouncementsQueue.filter(item => {
            return !queuedDismissals.some(queuedItem => queuedItem.keyMarketPlaceAnnouncements === item.keyMarketPlaceAnnouncements);
        });
    });

    addTask(dismissTask);
}, dismissDebounceDuration);

const dismissAnnouncements = (announcementIds: string[]): AppThunkAction<DismissAnnouncementsAction> => (dispatch, getState) => {
    const uniqueAnnouncementIds = [...new Set(announcementIds.filter(id => !!id))];
    const isLoggedIn = get(getState(), 'user.isLoggedIn', false);

    if (!uniqueAnnouncementIds.length) {
        return;
    }

    // Persist dismissals only for guest sessions.
    if (!isLoggedIn) {
        const currentDismissals = loadAnnouncementDismissals();
        const mergedDismissals = Array.from(new Set([...currentDismissals, ...uniqueAnnouncementIds]));
        saveAnnouncementDismissals(mergedDismissals);
    }

    publishAnnouncementSyncMessage({
        type: 'DISMISS',
        announcementIds: uniqueAnnouncementIds,
        isLoggedIn,
    });

    dispatch({ type: API_DISMISS_ANNOUNCEMENTS, announcementIds: uniqueAnnouncementIds });

    // Only queue for API if logged-in; KOU is resolved server-side from authenticated identity.
    if (isLoggedIn) {
        queueDismissedAnnouncements(uniqueAnnouncementIds);
        flushDismissedAnnouncements();
    }
};

const markAnnouncementsViewed = (): AppThunkAction<MarkAnnouncementsViewedAction> => (_dispatch, getState) => {
    const isLoggedIn = get(getState(), 'user.isLoggedIn', false);
    // Read ShowRedDot before dispatching so we know whether the backend POST is needed.
    const showRedDot = get(getState(), 'announcements.ShowRedDot', false);

    _dispatch({ type: API_MARK_ANNOUNCEMENTS_VIEWED });

    if (isLoggedIn) {
        // Only POST to backend when the red dot was actually showing (unread state).
        if (showRedDot) {
            const markViewedTask = APIManager.markAnnouncementsViewed().catch((error: Error) => {
                console.error('Failed to mark announcements as viewed', error);
            });
            addTask(markViewedTask);
        }

        publishAnnouncementSyncMessage({
            type: 'VIEWED',
            hasViewed: true,
            isLoggedIn: true,
        });

        const authChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('announcements_sync') : null;
        if (authChannel) {
            authChannel.postMessage({ type: 'VIEWED', hasViewed: true, isLoggedIn: true });
            authChannel.close();
        }
        return;
    }

    saveAnnouncementViewed(true);

    publishAnnouncementSyncMessage({
        type: 'VIEWED',
        hasViewed: true,
        isLoggedIn: false,
    });

    // BroadcastChannel stays as compatibility fallback.
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('announcements_sync') : null;
    if (channel) {
        channel.postMessage({ type: 'VIEWED', hasViewed: true, isLoggedIn: false });
        channel.close();
    }
};

// eslint-disable-next-line import/prefer-default-export
export { fetchAnnouncementsSummary, fetchAnnouncementsArchive, dismissAnnouncements, markAnnouncementsViewed };
